import { ref } from "vue";

/**
 * Runs async tasks while tracking a `loading` flag, applying the result only for
 * the most recent call, a slow task overtaken by a newer one is discarded.
 * `loading` is cleared only by the latest request.
 * Multi-step tasks get an `isCurrent()` predicate to skip the remaining steps once superseded.
 */
export function useSyncedTask() {
  const loading = ref(false);
  let requestCount = 0;

  function runSync<T>(
    task: (isCurrent: () => boolean) => Promise<T | undefined>,
    apply: (result: T) => void,
  ): Promise<void> {
    const requestId = ++requestCount;
    loading.value = true;
    return task(() => requestId === requestCount).then(
      (result) => {
        if (requestId === requestCount) {
          loading.value = false;
          if (result !== undefined) apply(result);
        }
      },
      (error) => {
        if (requestId !== requestCount) return;
        loading.value = false;
        throw error;
      },
    );
  }

  return { loading, runSync };
}

export async function runMinDelay<T>(task: Promise<T>, minDelay: number): Promise<T> {
  return (await Promise.all([task, new Promise((resolve) => setTimeout(resolve, minDelay))]))[0];
}
